import React, { useState, useEffect } from 'react';
import './App.css';
import './styles/AccountTypeSelection.css';
import SplashScreen from './components/SplashScreen';
import LoginForm from './components/LoginForm';
import SignupForm from './components/SignupForm';
import CommitmentScreen, { isCommitmentAccepted, clearCommitmentAccepted } from './components/CommitmentScreen';
import ProfessorRegistrationForm from './components/ProfessorRegistrationForm';
import MyRecord from './components/MyRecord';
import AuthService from './services/AuthService';

const activateUserAccount = (user) => {
  if (!user) return user;
  return {
    ...user,
    statut_compte: 'actif',
  };
};

/**
 * Application principale - Plateforme web React d'identification et de gestion des enseignants (Professeurs, Chefs des travaux, Assistants)
 * 
 * Sécurité des routes :
 * - Une fois connecté, impossible d'accéder au login/signup
 * - Seule la déconnexion manuelle permet de revenir au login
 * - La session persiste au rechargement de page (F5)
 * - Le token Bearer est automatiquement inclus dans les requêtes API
 * - Validation d'intégrité du token au démarrage
 * - Gestion automatique de l'expiration du token
 */
function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedAccountType, setSelectedAccountType] = useState('');
  const [isSessionRestored, setIsSessionRestored] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const [showCommitment, setShowCommitment] = useState(false);

  // 'record' = vue "Mon dossier" (défaut après login)
  // 'form'   = formulaire création / édition
  const [appView, setAppView] = useState('record');
  const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
  const [recordToEdit, setRecordToEdit] = useState(null); // données à pré-remplir en mode edit
  const [editRecordType, setEditRecordType] = useState(''); // 'Professeur' | 'Assistant' | 'CT'
  const [openMessagingOnRecord, setOpenMessagingOnRecord] = useState(false);

  // Vérifier s'il y a une session active au démarrage
  useEffect(() => {
    const initializeApp = () => {
      
      // Vérifier directement localStorage d'abord
      const rawToken = localStorage.getItem('authToken');
      const rawUser = localStorage.getItem('user');

      // Si un token est présent, considérer la session comme active
      if (rawToken) {
        if (rawUser) {
          try {
            const parsedUser = activateUserAccount(JSON.parse(rawUser));
            AuthService.saveSession(parsedUser);
            setCurrentUser(parsedUser);
            setIsLoggedIn(true);
          } catch (e) {
            setCurrentUser(null);
            setIsLoggedIn(false);
          }
        } else {
          setCurrentUser(null);
          setIsLoggedIn(false);
        }
      } else {
        // Pas de token en localStorage — fallback sur AuthService
        if (AuthService.isAuthenticated()) {
          const user = activateUserAccount(AuthService.getUser());
          AuthService.saveSession(user);
          setCurrentUser(user);
          setIsLoggedIn(true);
        } else {
          AuthService.logout();
        }
      }
      
      setIsSessionRestored(true);
      setIsInitializing(false);
    };

    // Lancer l'initialisation après un petit délai pour s'assurer que localStorage est prêt
    const timeout = setTimeout(initializeApp, 100);
    
    return () => clearTimeout(timeout);
  }, []);

  const handleLogout = () => {
    // Déconnexion complète via AuthService
    AuthService.logout();
    clearCommitmentAccepted();
    
    // Réinitialiser l'état de l'application
    setIsLoggedIn(false);
    setShowSignup(false);
    setCurrentUser(null);
    setSelectedAccountType('');
    setAppView('record');
    setFormMode('create');
    setRecordToEdit(null);
  };

  // Protection contre l'accès au login/signup si déjà connecté
  useEffect(() => {
    if (isSessionRestored && isLoggedIn && !isInitializing) {
      // L'utilisateur connecté ne peut pas accéder au formulaire de signup
      if (showSignup) {
        setShowSignup(false);
      }
    }
  }, [isLoggedIn, showSignup, isSessionRestored, isInitializing]);

  return (
    <div className="App">
      {isInitializing ? (
        <SplashScreen onFinish={() => {}} />
      ) : !isLoggedIn ? (
        showSignup ? (
          <SignupForm 
            onSignupSuccess={(userData) => {
              const activeUser = activateUserAccount(userData);
              AuthService.saveSession(activeUser);
              setCurrentUser(activeUser);
              setShowSignup(false);
              setIsLoggedIn(true);
              if (!isCommitmentAccepted()) setShowCommitment(true);
            }}
            onBackToLogin={() => setShowSignup(false)}
          />
        ) : (
          <LoginForm 
            onLoginSuccess={(userData) => {
              const activeUser = activateUserAccount(userData);
              AuthService.saveSession(activeUser);
              setCurrentUser(activeUser);
              setIsLoggedIn(true);
              if (!isCommitmentAccepted()) setShowCommitment(true);
            }}
            onSignupClick={() => setShowSignup(true)}
          />
        )
      ) : showCommitment ? (
        <CommitmentScreen onContinue={() => setShowCommitment(false)} />
      ) : (
        appView === 'record' ? (
          <MyRecord
            currentUser={currentUser}
            onCreateRecord={(type) => {
              setFormMode('create');
              setRecordToEdit(null);
              setEditRecordType(type || '');
              setAppView('form');
            }}
            onEditRecord={(record, type) => {
              setFormMode('edit');
              setRecordToEdit(record);
              setEditRecordType(type);
              setAppView('form');
            }}
            onUserUpdated={(updatedUser) => {
              setCurrentUser(updatedUser);
              setSelectedAccountType('');
              setFormMode('create');
              setRecordToEdit(null);
              setEditRecordType('');
            }}
            openMessagingOnRecord={openMessagingOnRecord}
            onMessagingOpened={() => setOpenMessagingOnRecord(false)}
            onLogout={handleLogout}
          />
        ) : (
          <ProfessorRegistrationForm
            onLogout={handleLogout}
            currentUser={currentUser}
            preselectedType={
              editRecordType ||
              selectedAccountType ||
              (currentUser && currentUser.type_de_compte === 'Chef de Travaux' ? 'CT' : currentUser && currentUser.type_de_compte) ||
              ''
            }
            formMode={formMode}
            preloadedRecord={recordToEdit}
            onRequestAccountTypeReset={() => setSelectedAccountType('')}
            onBackToRecord={() => {
              setAppView('record');
              setFormMode('create');
              setRecordToEdit(null);
              setEditRecordType('');
            }}
            onOpenMessaging={() => {
              setOpenMessagingOnRecord(true);
              setAppView('record');
            }}
          />
        )
      )}
    </div>
  );
}

export default App;
